import React, { ReactNode, useState, useCallback } from 'react';
import moment from 'moment';
import { ModalsKeys, Nullable } from 'global';
import { Button, Stack } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import { formatCurrency } from 'utils';
import { logError } from 'utils/logger';
import { calculateMarkupPercent, roundToTwoDecimals } from 'utils/financeCommissionCalculations';
import { useHistory } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { isNotNullOrUndefined } from 'utils/typeGuargs';
import { useNotifications } from 'modules/notification';
import { generatePersonaFullName } from 'utils/formatters';
import { GET_PROPOSAL_FOR_REVENUE } from 'modules/proposals';
import { ProposalSaleType } from '@trnsact/trnsact-shared-types';
import {
  AftermarketProposalData,
  AftermarketRevenueCardProps,
  DEFAULT_CATEGORY_ADDITIONALS,
  RevenueCardFormValues,
} from 'modules/aftermarket';
import { ProposalForRevenueResponse, ProposalForRevenueVariables } from 'modules/proposals/api/types';
import { useModal } from 'hooks/useModal';
import {
  FinanceProgramMarkupResponse,
  FinanceProgramMarkupVariables,
  GET_FP_MARKUP_BY_ID,
} from 'modules/financeProgram';
import {
  EquipmentListItem,
  EquipmentRevenueCardProps,
  EquipmentRevenueResponse,
  EquipmentRevenueVariables,
  EquipmentTotals,
  generateEquipmentName,
  GET_LIST_OF_EQUIPMENT_REVENUE_BY_VO_ID,
  useEquipmentActions,
} from 'modules/equipment';
import {
  VOApi,
  RevenueProfitCardProps,
  VOProposalOptionsListResponse,
  VOProposalOptionsListVariables,
} from 'modules/vendorOpportunity';
import {
  CreditSubmissionRevenue,
  CreditSubmissionRevenueResponse,
  CreditSubmissionRevenueVariables,
  CreditSubmissionSelectOptionsResponse,
  CreditSubmissionSelectOptionsVariables,
  FinanceProgramMarkup,
  FinanceProgramsSelectOptionsResponse,
  FinanceProgramsSelectOptionsVariables,
  FundingCardFormValues,
  FundingCardProps,
  GET_CREDIT_SUBMISSION_REVENUE,
  GET_CREDIT_SUBMISSIONS_SELECT_OPTIONS,
  GET_FINANCE_PROGRAMS_SELECT_OPTIONS,
  getFundedSubmissionOptions,
  useCreditSubmission,
} from 'modules/creditSubmission';
import { calculateCommissionFlat } from 'utils/financeCommissionCalculations';
import { calculateSalesCommissionFlat } from 'utils/equipmentCommissionCalculations';
import { DateFormats } from 'constants/common';
import { RevenueTabStyledContainer } from '../RevenueTabStyledContainer';

interface Props {
  voId: string;
  accountId: string;
  dynamicsVOId: string;
  submissionCard: (renderProps: FundingCardProps) => ReactNode;
  profitCard: (renderProps: RevenueProfitCardProps) => ReactNode;
  equipmentCard: (renderProps: EquipmentRevenueCardProps) => ReactNode;
  aftermarketCard: (renderProps: AftermarketRevenueCardProps) => ReactNode;
}

export const RevenueSummary = ({
  voId,
  accountId,
  profitCard,
  dynamicsVOId,
  equipmentCard,
  submissionCard,
  aftermarketCard,
}: Props) => {
  const { replace } = useHistory();
  const { showNotification } = useNotifications();
  const { onUpdateEquipment, isEquipmentUpdating } = useEquipmentActions();
  const { handleUpdateCreditSubmission, isUpdating } = useCreditSubmission();
  const { handleOpen: handleOpenLoadingModal, handleClose: handleCloseLoadingModal } = useModal(ModalsKeys.Load);
  const { handleOpen: handleOpenCalculatedRevenueModal } = useModal(ModalsKeys.CalculatedRevenueDialog);

  const [upsertVendorOpportunityRevenue] = useMutation(VOApi.upsertVendorOpportunityRevenue);

  // State for unified save functionality
  const [equipmentFormState, setEquipmentFormState] = useState<{
    isDirty: boolean;
    handleSave: () => Promise<void>;
    handleReset: VoidFunction;
    formValues: EquipmentListItem[];
  }>({ isDirty: false, handleSave: async () => {}, handleReset: () => {}, formValues: [] });

  const [financeFormState, setFinanceFormState] = useState<{
    isDirty: boolean;
    isDisabled: boolean;
    handleSave: () => Promise<void>;
    formValues: FundingCardFormValues;
  }>({ isDirty: false, isDisabled: true, handleSave: async () => {}, formValues: {} as FundingCardFormValues });

  const [aftermarketFormState, setAftermarketFormState] = useState<{
    isDirty: boolean;
    hasDataToSave: boolean;
    formValues: RevenueCardFormValues;
  }>({
    isDirty: false,
    hasDataToSave: false,
    formValues: { proposal: null, products: [], categoryAdditionals: DEFAULT_CATEGORY_ADDITIONALS },
  });

  const handleEquipmentFormStateChange = useCallback(
    (isDirty: boolean, handleSave: () => Promise<void>, handleReset: VoidFunction, formValues: EquipmentListItem[]) => {
      setEquipmentFormState({ isDirty, handleSave, handleReset, formValues });
    },
    []
  );

  const handleFinanceFormStateChange = useCallback(
    (isDirty: boolean, isDisabled: boolean, handleSave: () => Promise<void>, formValues: FundingCardFormValues) => {
      setFinanceFormState({ isDirty, isDisabled, handleSave, formValues });
    },
    []
  );

  const handleAftermarketFormStateChange = useCallback((formValues: RevenueCardFormValues, isDirty: boolean) => {
    const hasDataToSave = Object.values(formValues.categoryAdditionals ?? {}).some(vals => (vals?.markupFlat ?? 0) > 0);
    setAftermarketFormState({ formValues, isDirty, hasDataToSave });
  }, []);

  const handleUnifiedSave = useCallback(async () => {
    try {
      // Show loading modal to prevent user interaction
      handleOpenLoadingModal({ message: 'Saving revenue data, please wait...' });

      const saveTasks: Array<{ type: 'equipment' | 'finance'; promise: Promise<void> }> = [];

      if (equipmentFormState.isDirty) {
        saveTasks.push({ type: 'equipment', promise: equipmentFormState.handleSave() });
      }

      if (financeFormState.isDirty && !financeFormState.isDisabled) {
        saveTasks.push({ type: 'finance', promise: financeFormState.handleSave() });
      }

      if (saveTasks.length > 0 || aftermarketFormState.hasDataToSave) {
        // Use Promise.allSettled to capture which mutations succeeded
        const results = await Promise.allSettled(saveTasks.map(task => task.promise));

        // Track which mutations succeeded
        const succeededTasks = new Set<'equipment' | 'finance'>();
        const failedTasks: string[] = [];

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            succeededTasks.add(saveTasks[index].type);
          } else {
            failedTasks.push(saveTasks[index].type);
            logError(result.reason);
          }
        });

        // Determine which forms should be included in revenue mutation
        // Include if: (1) form was not dirty, OR (2) form was dirty AND mutation succeeded
        const shouldIncludeEquipment = !equipmentFormState.isDirty || succeededTasks.has('equipment');
        const shouldIncludeFinance = !financeFormState.isDirty || succeededTasks.has('finance');

        // Build revenue mutation input
        const input: any = {
          vendorOpportunityId: voId,
        };

        // Include equipment revenue details if conditions met
        if (shouldIncludeEquipment && equipmentFormState.formValues.length > 0) {
          input.equipmentRevenueDetails = equipmentFormState.formValues.map(equipment => {
            // Calculate salesCommissionFlat if it's null but salesCommissionPercent is not null
            let salesCommissionFlat = equipment.salesCommissionFlat;
            if (
              salesCommissionFlat === null &&
              equipment.salesCommissionPercent !== null &&
              equipment.markupFlat !== null &&
              equipment.markupFlat > 0
            ) {
              salesCommissionFlat = calculateSalesCommissionFlat(
                equipment.markupFlat,
                equipment.salesCommissionPercent
              );
            }

            return {
              equipmentId: equipment.id,
              salesRepCommission: salesCommissionFlat || 0,
            };
          });

          // Calculate total equipment revenue (markupFlat = retailPrice - wholesalePrice)
          input.totalEquipmentRevenue = equipmentFormState.formValues.reduce(
            (sum, equipment) => sum + (equipment.markupFlat || 0),
            0
          );
        }

        // Include finance revenue details if conditions met
        if (shouldIncludeFinance && financeFormState.formValues && financeFormState.formValues.lenderName) {
          const financeValues = financeFormState.formValues;

          // Calculate finance markup to match Profit card display
          const dealerMarkupFlat = financeValues.financeMarkupFlat || 0;

          // Calculate financeCommissionFlat if it's null but financeCommissionPercent is not null
          let financeCommissionFlat = financeValues.financeCommissionFlat;
          if (
            financeCommissionFlat === null &&
            financeValues.financeCommissionPercent !== null &&
            financeValues.fundedAmount !== null &&
            financeValues.fundedAmount > 0
          ) {
            financeCommissionFlat = calculateCommissionFlat(
              financeValues.fundedAmount,
              financeValues.financeCommissionPercent
            );
          }

          input.financeRevenueDetails = {
            submissionId: financeValues.fundedSubmission?.dynamicCSId || null,
            lender: financeValues.lenderName || null,
            fundedAmount: financeValues.fundedAmount || 0,
            financeMarkup: dealerMarkupFlat,
            financeCommission: financeCommissionFlat || 0,
            dateFounded: financeValues.dateFunded,
            dateCommissionReceived: financeValues.commissionReceivedDate,
            financeProgramId: financeValues.financeProgram?.value || null,
            financeProgramName: financeValues.financeProgramName || null,
          };

          // Total finance revenue matches the Profit card display (dealer markup)
          input.totalFinanceRevenue = dealerMarkupFlat;
        }

        // Include aftermarket/protection products revenue
        if (aftermarketFormState.formValues.products && aftermarketFormState.formValues.products.length > 0) {
          const { total, details } = aftermarketFormState.formValues.products.reduce(
            (acc, product) => {
              const markup = product.markupFlat || 0;
              acc.total += markup;
              acc.details.push({
                proposalProductId: product.id,
                cost: product.cost || 0,
                markup,
              });
              return acc;
            },
            { total: 0, details: [] as Array<{ proposalProductId: string; cost: number; markup: number }> }
          );

          input.totalProtectionProductsRevenue = total;
          input.protectionProductsRevenueDetails = details;
        }

        // Include manually entered per-category additional products revenue
        const categoryAdditionals = aftermarketFormState.formValues.categoryAdditionals;
        if (categoryAdditionals) {
          const additionalDetails = Object.entries(categoryAdditionals)
            .filter(([, values]) => (values?.markupFlat ?? 0) > 0)
            .map(([category, values]) => ({
              category,
              markup: values!.markupFlat || 0,
              cost: values!.totalCost ?? null,
            }));

          if (additionalDetails.length > 0) {
            input.additionalProtectionProductsRevenueDetails = additionalDetails;
            input.totalProtectionProductsRevenue =
              (input.totalProtectionProductsRevenue ?? 0) +
              additionalDetails.reduce((sum, entry) => sum + entry.markup, 0);
          }
        }

        // Call revenue mutation if we have any data to include
        if (
          input.equipmentRevenueDetails ||
          input.financeRevenueDetails ||
          input.protectionProductsRevenueDetails ||
          input.additionalProtectionProductsRevenueDetails
        ) {
          await upsertVendorOpportunityRevenue({ variables: { input } });
        }

        // Show appropriate notification based on results
        if (failedTasks.length === 0) {
          showNotification('Revenue saved successfully');
        } else if (succeededTasks.size > 0) {
          showNotification(`Partially saved. Failed to save: ${failedTasks.join(', ')}`, { type: 'warning' });
        } else {
          showNotification('Failed to save revenue', { type: 'error' });
        }
      }
    } catch (error) {
      logError(error);
      showNotification('Failed to save revenue', { type: 'error' });
    } finally {
      // Always close the loading modal
      handleCloseLoadingModal();
    }
  }, [
    equipmentFormState,
    financeFormState,
    aftermarketFormState,
    voId,
    upsertVendorOpportunityRevenue,
    showNotification,
    handleOpenLoadingModal,
    handleCloseLoadingModal,
  ]);

  const isSaveDisabled =
    !equipmentFormState.isDirty &&
    (!financeFormState.isDirty || financeFormState.isDisabled) &&
    !aftermarketFormState.hasDataToSave;
  const isSaving = isEquipmentUpdating || isUpdating;

  const [fetchCreditSubmissions, { data: creditSubmissionsData, loading: isCreditSubmissionsLoading }] = useLazyQuery<
    CreditSubmissionSelectOptionsResponse,
    CreditSubmissionSelectOptionsVariables
  >(GET_CREDIT_SUBMISSIONS_SELECT_OPTIONS, {
    variables: { id: dynamicsVOId },
  });

  const { data: financeProgramsData, loading: isFinanceProgramsLoading } = useQuery<
    FinanceProgramsSelectOptionsResponse,
    FinanceProgramsSelectOptionsVariables
  >(GET_FINANCE_PROGRAMS_SELECT_OPTIONS, { variables: { accountId } });

  const [
    fetchProposalOptions,
    { data: proposalOptionsResponse = { VOProposalOptionsList: [] }, loading: isOptionsLoading },
  ] = useLazyQuery<VOProposalOptionsListResponse, VOProposalOptionsListVariables>(VOApi.fetchProposalOptions, {
    variables: { voId },
  });

  const [
    onFetchEquipmentList,
    { data: equipmentListResponse = { equipmentRevenues: [] }, loading: isEquipmentListLoading },
  ] = useLazyQuery<EquipmentRevenueResponse, EquipmentRevenueVariables>(GET_LIST_OF_EQUIPMENT_REVENUE_BY_VO_ID, {
    variables: { VOId: voId },
  });

  const [onFetchFinanceProgramMarkup, { data: fpMarkupResponse, loading: isFPMarkupLoading }] = useLazyQuery<
    FinanceProgramMarkupResponse,
    FinanceProgramMarkupVariables
  >(GET_FP_MARKUP_BY_ID);

  const [
    onFetchCreditSubmissionRevenue,
    {
      data: creditSubmissionRevenueResponse,
      refetch: refetchCreditSubmissionRevenue,
      loading: isCreditSubmissionRevenueLoading,
    },
  ] = useLazyQuery<CreditSubmissionRevenueResponse, CreditSubmissionRevenueVariables>(GET_CREDIT_SUBMISSION_REVENUE);

  const [
    onFetchProposalById,
    {
      loading: isProposalRevenueDataLoading,
      data: proposalRevenueInfoResponse = { proposalRevenueInfo: { lenderPaymentOptions: [] } },
    },
  ] = useLazyQuery<ProposalForRevenueResponse, ProposalForRevenueVariables>(GET_PROPOSAL_FOR_REVENUE);

  const handleFetchFinanceProgramMarkup = async (id: string): Promise<Nullable<FinanceProgramMarkup>> => {
    try {
      const { data: fpMarkupResponse } = await onFetchFinanceProgramMarkup({ variables: { id } });

      if (!fpMarkupResponse) return null;

      return getFPMarkup(fpMarkupResponse, creditSubmissionRevenue.approvedAmount ?? 0);
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const onUpdateCreditSubmission = async (
    dynamicCSId: string,
    dto: { commissionReceivedDate: Nullable<string>; financeCommissionPercent: Nullable<number> }
  ) => {
    const fundedStage = 804790020;

    try {
      await handleUpdateCreditSubmission(dynamicCSId, {
        stage: fundedStage,
        fundedTimestamp: moment().toISOString(),
        ...dto,
      });
      showNotification('Successfully updated!');
      await refetchCreditSubmissionRevenue();
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleGetSubmissionRevenue = async (id: string): Promise<Nullable<CreditSubmissionRevenue>> => {
    try {
      const { data: creditSubmissionRevenueResponse } = await onFetchCreditSubmissionRevenue({
        variables: { creditSubmissionId: id },
      });

      return normalizeCSRevenue(creditSubmissionRevenueResponse);
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const handleGetProposal = async (proposalId: string): Promise<Nullable<AftermarketProposalData>> => {
    try {
      const { data: proposalRevenueResponse } = await onFetchProposalById({ variables: { id: proposalId } });

      if (!proposalRevenueResponse) return null;

      return normalizeProposalRevenueResponse(proposalRevenueResponse);
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const handleRedirectToDesking = () => {
    replace({ hash: '#desking', pathname: `/vo/${voId}` });
  };

  const handleRedirectToGeneralTab = () => {
    replace({ hash: '#general', pathname: `/vo/${voId}` });
  };

  const handleRedirectToDashboard = () => {
    replace({ pathname: `/dashboard` });
  };

  const handleUpdateEquipmentList = async (dtoList: EquipmentListItem[]) => {
    try {
      await Promise.all(
        dtoList.map(dto =>
          onUpdateEquipment(
            {
              vendorOpportunityId: voId,
              ...mapEquipmentListToUpdateDto(dto),
              dynamicsVendorOpportunityId: dynamicsVOId,
            },
            { withNotification: false }
          )
        )
      );

      showNotification('Equipment successfully updated!');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const proposalListOptions = generateProposalOptionsList(proposalOptionsResponse);
  const creditSubmissionRevenue = normalizeCSRevenue(creditSubmissionRevenueResponse);
  const proposalRevenueData = normalizeProposalRevenueResponse(proposalRevenueInfoResponse);
  const equipmentData = normalizeEquipmentRevenuesResponse(equipmentListResponse);
  const fpMarkup = getFPMarkup(fpMarkupResponse, creditSubmissionRevenue.approvedAmount ?? 0);
  const fundedSubmissionOptions = getFundedSubmissionOptions(creditSubmissionsData?.creditSubmissionsSelectOptions);

  const getRevenueCardInitialFormValues = async (): Promise<RevenueCardFormValues> => {
    const defaultValues: RevenueCardFormValues = {
      proposal: null,
      products: [],
      categoryAdditionals: DEFAULT_CATEGORY_ADDITIONALS,
    };

    try {
      const { data: proposalOptionsResponse } = await fetchProposalOptions();

      if (!proposalOptionsResponse) return defaultValues;

      const [mostResentProposal] = generateProposalOptionsList(proposalOptionsResponse);

      if (!mostResentProposal) return defaultValues;

      const { data: proposalResponse } = await onFetchProposalById({ variables: { id: mostResentProposal.value } });

      if (!proposalResponse) return defaultValues;

      const proposalRevenueData = normalizeProposalRevenueResponse(proposalResponse);

      return {
        proposal: mostResentProposal,
        products: proposalRevenueData.products,
        categoryAdditionals: DEFAULT_CATEGORY_ADDITIONALS,
      };
    } catch (error) {
      logError(error);
      return defaultValues;
    }
  };

  const getInitialFundingCardFormValues = async (): Promise<FundingCardFormValues> => {
    const defaultValues: FundingCardFormValues = {
      lenderName: '',
      dateFunded: null,
      financeProgram: null,
      financeProgramName: null,
      fundedAmount: null,
      fundedSubmission: null,
      financeMarkupFlat: null,
      commissionReceivedDate: null,
      financeCommissionFlat: null,
      financeMarkupPercentage: null,
      financeCommissionPercent: null,
    };

    try {
      const { data: creditSubmissionsResponse } = await fetchCreditSubmissions();

      if (!creditSubmissionsResponse) return defaultValues;

      const [mostResentCS] = getFundedSubmissionOptions(creditSubmissionsResponse?.creditSubmissionsSelectOptions);

      if (!mostResentCS) return defaultValues;

      const { data: creditSubmissionRevenueResponse } = await onFetchCreditSubmissionRevenue({
        variables: { creditSubmissionId: mostResentCS.value },
      });

      if (!creditSubmissionRevenueResponse) return defaultValues;

      const creditSubmissionRevenue = normalizeCSRevenue(creditSubmissionRevenueResponse);

      const { lenderName, fundedTimestamp, commissionReceivedDate, financeCommissionPercent, approvedAmount } =
        creditSubmissionRevenue;

      return {
        ...defaultValues,
        lenderName,
        commissionReceivedDate,
        financeCommissionPercent,
        dateFunded: fundedTimestamp,
        fundedAmount: approvedAmount,
        fundedSubmission: mostResentCS,
      };
    } catch (error) {
      logError(error);
      return defaultValues;
    }
  };

  const getInitialEquipmentList = async (): Promise<{ equipments: EquipmentListItem[] }> => {
    const defaultValues: { equipments: EquipmentListItem[] } = { equipments: [] };

    try {
      const { data: equipmentResponse } = await onFetchEquipmentList();

      if (!equipmentResponse) return defaultValues;

      const equipmentData = normalizeEquipmentRevenuesResponse(equipmentResponse);

      return { equipments: equipmentData.equipmentList };
    } catch (error) {
      logError(error);
      return defaultValues;
    }
  };
  return (
    <RevenueTabStyledContainer>
      <Stack gap="1rem">
        {equipmentCard({
          ...equipmentData,
          isEquipmentListLoading,
          getInitialEquipmentList,
          handleUpdateEquipmentList,
          handleRedirectToGeneralTab,
          isEquipmentListUpdating: isEquipmentUpdating,
          onFormStateChange: handleEquipmentFormStateChange,
        })}

        {submissionCard({
          fundedSubmissionOptions,
          onUpdateCreditSubmission,
          handleGetSubmissionRevenue,
          handleFetchFinanceProgramMarkup,
          getInitialFundingCardFormValues,
          financeProgramsOptions: financeProgramsData?.financeProgramsSelectOptions ?? [],
          loading: {
            isFPMarkupLoading,
            isFinanceProgramsLoading,
            isCreditSubmissionsLoading,
            isCSStatusUpdating: isUpdating,
            isCreditSubmissionRevenueLoading,
          },
          onFormStateChange: handleFinanceFormStateChange,
        })}

        {aftermarketCard({
          isOptionsLoading,
          handleGetProposal,
          proposalListOptions,
          isProposalRevenueDataLoading,
          getRevenueCardInitialFormValues,
          onCreateProductQuote: handleRedirectToDesking,
          onFormStateChange: handleAftermarketFormStateChange,
        })}
      </Stack>

      <Stack gap="1rem">
        {profitCard({
          finance: fpMarkup.flat,
          handleRedirectToDashboard,
          equipment: equipmentData.totals.markupFlat,
          aftermarket: proposalRevenueData.totals.markupFlat,
          loading: { isProposalRevenueDataLoading, isEquipmentUpdating },
          onUnifiedSave: handleUnifiedSave,
          isSaveDisabled,
          isSaving,
        })}

        <Button
          color="primary"
          variant="contained"
          fullWidth
          startIcon={<CalculateIcon />}
          onClick={() => handleOpenCalculatedRevenueModal({ vendorOpportunityId: voId })}
        >
          Auto-populate Revenue Data
        </Button>
      </Stack>
    </RevenueTabStyledContainer>
  );
};

function sortProposalsByCreateDateTime(proposals: VOProposalOptionsListResponse['VOProposalOptionsList']) {
  return proposals.toSorted((a, b) => moment(b.createdDateTime).valueOf() - moment(a.createdDateTime).valueOf());
}

function generateProposalLabel({
  createdDateTime,
  lastName,
  firstName,
  amountRequested,
  status,
}: VOProposalOptionsListResponse['VOProposalOptionsList'][number]) {
  return [
    moment(createdDateTime).format(DateFormats.DateTimeShort12Hour),
    generatePersonaFullName({ lastName, firstName }),
    formatCurrency(amountRequested, true),
    status,
  ]
    .filter(isNotNullOrUndefined)
    .join(' | ');
}

function generateProposalOptionsList(response: VOProposalOptionsListResponse) {
  if (!Array.isArray(response.VOProposalOptionsList)) return [];

  const onlyAcceptedWithAftermarketMenu = response.VOProposalOptionsList.filter(({ status, saleType }) =>
    [ProposalSaleType.FinanceWithAftermarketMenu, ProposalSaleType.CashWithAftermarketMenu].includes(saleType)
  );

  const sortedProposals = sortProposalsByCreateDateTime(onlyAcceptedWithAftermarketMenu);

  return sortedProposals.map(proposal => ({
    value: proposal.value,
    label: generateProposalLabel(proposal),
  }));
}

function getFPMarkup(response: FinanceProgramMarkupResponse | undefined, approvedAmount: number) {
  if (!response) return { flat: 0, percent: 0 };

  const fpMarkupPercent = response?.financeProgramMarkup?.paymentOptionsConfiguration?.dealerMarkup as Nullable<number>;

  if (fpMarkupPercent === null) return { flat: 0, percent: 0 };

  return {
    percent: fpMarkupPercent,
    flat: (fpMarkupPercent * approvedAmount) / 100,
  };
}

function normalizeCSRevenue(response: CreditSubmissionRevenueResponse | undefined): CreditSubmissionRevenue {
  return {
    lenderName: response?.creditSubmissionRevenue?.lenderName ?? '',
    approvedAmount: response?.creditSubmissionRevenue?.approvedAmount ?? null,
    financeCommissionPercent: response?.creditSubmissionRevenue?.financeCommissionPercent ?? null,
    commissionReceivedDate: response?.creditSubmissionRevenue?.commissionReceivedDate
      ? moment(+response?.creditSubmissionRevenue.commissionReceivedDate).format(DateFormats.Short)
      : null,
    fundedTimestamp: response?.creditSubmissionRevenue?.fundedTimestamp
      ? moment(+response?.creditSubmissionRevenue.fundedTimestamp).format(DateFormats.Short)
      : null,
  };
}

function normalizeEquipmentRevenuesResponse(response: EquipmentRevenueResponse) {
  const initialNormalizedValues = {
    equipmentList: [],
    totals: { salePrice: 0, wholesalePrice: 0, markupFlat: 0, salesCommissionFlat: 0 },
  };

  if (!Array.isArray(response.equipmentRevenues)) return initialNormalizedValues;

  return response.equipmentRevenues.reduce<{
    totals: EquipmentTotals;
    equipmentList: EquipmentListItem[];
  }>(
    (acc, equipment) => {
      const markupFlat = equipment.retailPrice - equipment.totalWholesaleCost;
      const salesCommissionFlat = markupFlat * ((equipment.salesCommissionPercent ?? 0) / 100);

      const item: EquipmentListItem = {
        id: equipment.id,
        markupFlat,
        dateSold: null,
        salesCommissionFlat,
        saleRep: equipment.saleRep,
        salePrice: equipment.retailPrice,
        name: generateEquipmentName(equipment),
        wholesalePrice: equipment.totalWholesaleCost,
        salesCommissionPercent: equipment.salesCommissionPercent ?? 0,
        markupPercent: markupFlat <= 0 ? 0 : (markupFlat / equipment.retailPrice) * 100,
      };

      acc.equipmentList.push(item);

      acc.totals.markupFlat += item.markupFlat || 0;
      acc.totals.salePrice += item.salePrice || 0;
      acc.totals.wholesalePrice += item.wholesalePrice || 0;
      acc.totals.salesCommissionFlat += item.salesCommissionFlat || 0;

      return acc;
    },
    { ...initialNormalizedValues }
  );
}

function normalizeProposalRevenueResponse(response: ProposalForRevenueResponse) {
  const initialNormalizedProposalRevenueData = { products: [], totals: { markupFlat: 0 } };

  const dataAboutProducts = response.proposalRevenueInfo.lenderPaymentOptions.find(
    option => !!option?.paymentCriteriaSelected
  );

  if (!dataAboutProducts) return initialNormalizedProposalRevenueData;

  return (
    (dataAboutProducts.paymentCriteriaSelected?.menuOption?.products ?? []).reduce<AftermarketProposalData>(
      (acc, product) => {
        const markupFlat = roundToTwoDecimals(Math.max(0, product.retailCost - product.cost));

        const mappedProduct = {
          markupFlat,
          name: product.title,
          id: product.proposalProductId,
          cost: product.cost,
          category: product.category,
          markupPercent: calculateMarkupPercent(markupFlat, product.cost),
          dateSold: moment(+dataAboutProducts.createdDateTime).format(DateFormats.DateTimeShort12Hour),
        };

        acc.products.push(mappedProduct);
        acc.totals.markupFlat += markupFlat;

        return acc;
      },
      { ...initialNormalizedProposalRevenueData }
    ) ?? initialNormalizedProposalRevenueData
  );
}

function mapEquipmentListToUpdateDto({ wholesalePrice, id, salePrice, salesCommissionPercent }: EquipmentListItem) {
  return {
    quantity: 1,
    equipmentId: id,
    salesCommissionPercent,
    retailPrice: salePrice,
    totalWholesaleCost: wholesalePrice,
  };
}
